import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Assessment,
  Star,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface KPI {
  label: string;
  value: number;
  change: number;
  trend: 'up' | 'down';
  format: 'currency' | 'number' | 'percentage';
}

interface RevenueTrend {
  month: string;
  revenue: number;
  target: number;
  forecast: number;
}

interface TeamPerformance {
  user_name: string;
  deals_closed: number;
  revenue: number;
  win_rate: number;
}

const ExecutiveDashboardPage: React.FC = () => {
  const [period, setPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

    // Fetch KPIs
  const { data: kpis, isLoading: kpisLoading } = useQuery({
    queryKey: ['executive-kpis', period],
    queryFn: async () => {
      try {
        const response = await baseClient.get<any>(`/executive/kpis?period=${period}`);
        return response?.data ?? response ?? [];
      } catch {
        return [];
      }
    }
  });

  // Fetch revenue trends
  const { data: revenueTrends, isLoading: revenueLoading } = useQuery({
    queryKey: ['revenue-trends', period],
    queryFn: async () => {
      try {
        const response = await baseClient.get<any>(`/executive/revenue-trends?period=${period}`);
        return response?.data ?? response ?? [];
      } catch {
        return [];
      }
    }
  });

  // Fetch team performance
  const { data: teamPerformance, isLoading: teamLoading } = useQuery({
    queryKey: ['team-performance', period],
    queryFn: async () => {
      try {
        const response = await baseClient.get<any>(`/executive/team-performance?period=${period}`);
        return response?.data ?? response ?? [];
      } catch {
        return [];
      }
    }
  });

  const formatValue = (value: number, format: string) => {
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString();
    }
  };

  const getIcon = (label: string) => {
    if (label.toLowerCase().includes('revenue')) return <AttachMoney />;
    if (label.toLowerCase().includes('team')) return <People />;
    if (label.toLowerCase().includes('performance')) return <Assessment />;
    return <Star />;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Executive Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={period}
            label="Time Period"
            onChange={(e) => setPeriod(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
          >
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        {kpisLoading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : kpis && kpis.length > 0 ? (
          kpis.map((kpi, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box sx={{ color: 'primary.main' }}>
                      {getIcon(kpi.label)}
                    </Box>
                    <Chip
                      icon={kpi.trend === 'up' ? <TrendingUp /> : <TrendingDown />}
                      label={`${kpi.change > 0 ? '+' : ''}${kpi.change.toFixed(1)}%`}
                      color={kpi.trend === 'up' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="h4" gutterBottom>
                    {formatValue(kpi.value, kpi.format)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {kpi.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Alert severity="info">No KPI data available</Alert>
          </Grid>
        )}
      </Grid>

      {/* Revenue Trends Chart */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Revenue Trends
        </Typography>
        {revenueLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : revenueTrends && revenueTrends.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Actual Revenue" strokeWidth={2} />
              <Line type="monotone" dataKey="target" stroke="#82ca9d" name="Target" strokeWidth={2} />
              <Line type="monotone" dataKey="forecast" stroke="#ffc658" name="Forecast" strokeWidth={2} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Alert severity="info">No revenue trend data available</Alert>
        )}
      </Paper>

      {/* Team Performance */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Team Performance
        </Typography>
        {teamLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : teamPerformance && teamPerformance.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={teamPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="user_name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="deals_closed" fill="#8884d8" name="Deals Closed" />
                <Bar dataKey="revenue" fill="#82ca9d" name="Revenue ($)" />
              </BarChart>
            </ResponsiveContainer>
            <Box mt={3}>
              <Grid container spacing={2}>
                {teamPerformance.map((member, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {member.user_name}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            Deals Closed
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {member.deals_closed}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            Revenue
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            ${member.revenue.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Win Rate
                          </Typography>
                          <Chip
                            label={`${member.win_rate.toFixed(1)}%`}
                            color={member.win_rate >= 50 ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        ) : (
          <Alert severity="info">No team performance data available</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ExecutiveDashboardPage;
